import { Translations } from "../logic/Translations";
import { ApiListItem } from "../model";
import { progressNotification } from "../ui";
import { Ajax } from "../api/infra/";
import { Objects } from "./Objects";
import { ODataQueryOptions } from "./ODataQueryOptions";
import { ODataResult } from "./ODataResult";

/** Provides helpers for handling OData queries */
export class OData {

  /**
   * Makes an OData query to the the given service URL.
   * @param baseUrl The service URL that is used, e.g. "/v03/api/taxcards/current".
   * @param query The options for the query: Filter, search sort etc.
   * This is either a strongly typed object or query string that is added directly.
   * @param ajax The Ajax implementation that is used for HTTP get.
   */
  public static getOData<T>(baseUrl: string, query: ODataQueryOptions | string | undefined, ajax: Ajax): Promise<ODataResult<T>> {
    let fullPath;
    if (typeof query === "string") {
      query = query.trim();
      if (query.length > 0 && query.substr(0, 1) === "?") {
        query = query.substr(1);
      }
      fullPath = (query.length === 0) ? baseUrl : (baseUrl + "?" + query);
    } else {
      query = query || {};
      fullPath = OData.getUrl(baseUrl, query);
    }
    return ajax.getJSON(fullPath).then((data) => {
      return OData.getODataResult<T>(data);
    });
  }

  /**
   * Export items: Fetches multiple pages (of 50 items) from the storage to achieve high $top (all if zero / null).
   * @param baseUrl The service URL that is used, e.g. "/v03/api/taxcards/current".
   * @param options The options for the query: Filter, search sort etc.
   * @param ajax The Ajax implementation that is used for HTTP get.
   * @param notify Function for notifying progress.
   */
  public static exportItems(baseUrl: string, options: ODataQueryOptions, ajax: Ajax, notify: progressNotification<void> = null): Promise<ApiListItem[]> {
    const items: ApiListItem[] = [];
    const pageSize = 50;
    options = options ? Objects.copy(options) : {};
    const maxLength = options.$top || 0;
    options.$top = pageSize;
    options.$skip = options.$skip || 0;

    const next = (): Promise<void> => {
      return this.getOData(baseUrl, options, ajax).then((odataResult) => {
        items.push(...odataResult.items);
        if (notify) {
          notify(Translations.get("SALAXY.UI_Terms.wait"), items.length - 1, null);
        }
        if (!odataResult.items.length || (maxLength > 0 && maxLength === items.length)) {
          return Promise.resolve();
        }
        options.$skip = items.length;
        options.$top = maxLength <= 0 ? pageSize : ((maxLength - items.length) > pageSize ? pageSize : maxLength - items.length);
        return next();
      });
    };
    return next().then(() => {
      return items;
    });
  }

  /**
   * Export items: Fetches multiple pages (server default page size) from the storage to achieve high $top (all if zero / null).
   * @param odataMethod The OData method that is called. Note that Salaxy API methods need the this, so you will need to pass
   * e.g. `(options) => srv.getOData(options)`, not simply `srv.getOData` because the call needs `this`.
   * @param options The options for the query: Filter, search sort etc.
   * Use $top to restrict the amount of items
   * Note that $top currently only restricts within page size (e.g. 50, 100), not exactly to the number of items. This may later change without being a breaking change.
   * @param notify Optional function for notifying progress. Called before each page await.
   */
  public static async getAllPages<T>(odataMethod: (options: ODataQueryOptions) => Promise<ODataResult<T>>, options: ODataQueryOptions, notify?: progressNotification<void>): Promise<ODataResult<T>> {
    // const pageSize = 50;
    options = options ? Objects.copy(options) : {};
    const maxLength = options.$top || 0;
    if (options.$top) {
      delete(options.$top);
    }
    if (notify) {
      notify(Translations.get("SALAXY.UI_Terms.wait"), 0);
    }
    const result = await odataMethod(options);
    while (result.nextPageLink) {
      options.$skip = result.items.length;
      // We could usee this if we wanted to use $top instead of NextPageLink
      // options.$top = maxLength <= 0 ? pageSize : ((maxLength - result.items.length) > pageSize ? pageSize : maxLength - result.items.length);
      if (notify) {
        notify(Translations.get("SALAXY.UI_Terms.wait"), result.items.length - 1, result.count);
      }
      const nextPage = await odataMethod(options);
      result.count = nextPage.count;
      result.items = [...result.items, ...nextPage.items];
      result.nextPageLink = nextPage.nextPageLink;
      if (!nextPage.items.length || (maxLength > 0 && maxLength <= result.items.length)) {
        return result;
      }
    }
    return result;
  }

  /**
   * Gets the result from Ajax result (json) to strongly typed result.
   * @param data Result data coming from the ajax call.
   * This may be either an OData object with count and next page link or just an array depending on the call.
   */
  public static getODataResult<T>(data: ODataResult<T> | T[]): ODataResult<T> {
    const result: ODataResult<T> = {
      items: [],
    };
    if ((data as ODataResult<T>).items) {
      data = data as ODataResult<T>;
      result.items = data.items;
      result.count = data.count || null;
      result.nextPageLink = data.nextPageLink || null;
    } else if ((data as any).value) {
      // TODO: Need to check this OData v4 format. Is this really the format supported by Azure services etc.?
      const v4Data = data as any;
      result.items = v4Data.value;
      result.count = v4Data["@odata.count"] || null;
      result.nextPageLink = v4Data["@odata.nextLink"] || null;
    } else if (Array.isArray(data)) {
      result.items = data;
      result.count = null;
      result.nextPageLink = null;
    } else {
      throw Error("Unexpected result: " + typeof (data));
    }
    return result;
  }

  /**
   * Escapes the Lucene search language special characters in search text values.
   * Special characters in Lucene Full syntax are: "+-&|!(){}[]^"~*?:\/"
   * and in simple syntax: "-*+|"()'\"
   * @param searchValue Search text value to escape for Lucene.
   * @param isSimpleSyntax If true, only escapes the necessary characters for simple syntax.
   */
  public static escapeSearchValue(searchValue: string, isSimpleSyntax = false): string {
    // TODO: Escapes here are probably really useless, but would need testing.
    if (isSimpleSyntax) {
      // eslint-disable-next-line no-useless-escape
      return searchValue.replace(/[\-\*\+\|\(\)\'\\]/g, (substring) => "\\" + substring);
    }
    // eslint-disable-next-line no-useless-escape
    return searchValue.replace(/[\+\-\&\|\!\(\)\{\}\[\]\^\"\~\*\?\:\\\/]/g, (substring) => "\\" + substring);
  }

  /**
   * Gets the URL including the query string using the provided OData query options.
   * @param baseUrl Base URL of the service to which the query is added.
   * If null, the returned URL is the query string part starting with the question mark
   * ...or if none of the options is set, returns just an empty string.
   * @param queryOptions options based on which the query is constructed.
   */
  public static getUrl(baseUrl: string, queryOptions: ODataQueryOptions): string {
    queryOptions = queryOptions || {};
    baseUrl = (baseUrl || "").trim();
    const queries: string[] = [];
    if (queryOptions.$count) {
      queries.push("$count=true");
    }
    if (queryOptions.$expand) {
      queries.push("$expand=" + queryOptions.$expand);
    }
    if (queryOptions.$filter) {
      queries.push("$filter=" + queryOptions.$filter);
    }
    if (queryOptions.$orderby) {
      queries.push("$orderby=" + queryOptions.$orderby);
    }
    if (queryOptions.$select) {
      queries.push("$select=" + queryOptions.$select);
    }
    if (queryOptions.$skip) {
      queries.push(`$skip=${queryOptions.$skip}`);
    }
    if (queryOptions.$top) {
      queries.push(`$top=${queryOptions.$top}`);
    }
    if (queryOptions.fullSearch) {
      queries.push("$search=" + encodeURIComponent(queryOptions.fullSearch));
    } else if (queryOptions.$search) {
      let search = queryOptions.$search;
      if (search.indexOf("\"") >= 0) {
        // phrase search
        queries.push("$search=" + encodeURIComponent(`${search}`));
      } else {
        search = this.escapeSearchValue(queryOptions.$search, true);
        // HACK: This is probably not necessary: The searches should work simply by escaping, but
        //       we must test the behavior of "@" (email) and "-" (official id) before removing this.
        //       Also note that $search may be depracted in favor of fullSearch.
        // search by phrase, term and wildcard
        search = search.endsWith("*") ? search.substring(0, search.length - 1) : search;
        queries.push("$search=" + encodeURIComponent(`"${search}" | ${search} | ${search}*`));
      }
    }
    return baseUrl + (queries.length === 0 ? "" : (baseUrl.indexOf("?") >= 0 ? "&" : "?") + queries.join("&"));
  }

}
